import { useState, useRef, useEffect, useMemo } from 'react';
import { SABOTAGE_OPTIONS, MLB_SABOTAGE_OPTIONS, SabotageOption, SabotageRarity } from '@/types/league';
import { useSport } from '@/contexts/SportContext';
import { trackEvent } from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Rarity tier weights — total probability per tier
const RARITY_WEIGHTS: Record<SabotageRarity, number> = {
  common: 0.50,
  rare: 0.35,
  legendary: 0.15,
};

/** Build per-card weights from rarity tiers, returns array aligned with options.
 *  SAB-413: `excluded` are cards the team has already used this cooldown window
 *  (e.g. Legendaries already applied this week). Excluded cards get weight 0;
 *  the rarity weight of a fully-excluded tier redistributes evenly across
 *  tiers that still have cards available, so the totals still sum to ~1.0
 *  and the fallback at the end of weightedRandomIndex doesn't land on a
 *  zero-weight segment. */
function buildCardWeights(options: SabotageOption[], excluded?: Set<string>): number[] {
  const counts: Record<SabotageRarity, number> = { common: 0, rare: 0, legendary: 0 };
  for (const o of options) {
    if (excluded?.has(o.name)) continue;
    counts[o.rarity || 'common']++;
  }

  // Redistribute the weight of any fully-excluded tier across remaining tiers.
  const adjusted: Record<SabotageRarity, number> = { ...RARITY_WEIGHTS };
  let orphaned = 0;
  let liveTiers = 0;
  for (const tier of ['common', 'rare', 'legendary'] as SabotageRarity[]) {
    if (counts[tier] === 0) {
      orphaned += adjusted[tier];
      adjusted[tier] = 0;
    } else {
      liveTiers++;
    }
  }
  if (orphaned > 0 && liveTiers > 0) {
    const share = orphaned / liveTiers;
    for (const tier of ['common', 'rare', 'legendary'] as SabotageRarity[]) {
      if (counts[tier] > 0) adjusted[tier] += share;
    }
  }

  return options.map(o => {
    if (excluded?.has(o.name)) return 0;
    const r = o.rarity || 'common';
    return counts[r] > 0 ? adjusted[r] / counts[r] : 0;
  });
}

/** Weighted random index selection */
function weightedRandomIndex(weights: number[]): number {
  const roll = Math.random();
  let cumulative = 0;
  for (let i = 0; i < weights.length; i++) {
    cumulative += weights[i];
    if (roll < cumulative) return i;
  }
  return weights.length - 1; // floating-point safety
}

interface SabotageWheelProps {
  onSabotageSelected: (sabotage: SabotageOption) => void;
  /** Called when the wheel lands but before user confirms. Parent can show confirm/cancel UI. */
  onSpinResult?: (sabotage: SabotageOption) => void;
  /** If true, the wheel will NOT auto-call onSabotageSelected after landing. Parent must handle confirm. */
  deferApply?: boolean;
  cost?: number;
  disabled?: boolean;
  disabledMessage?: string;
  /** SAB-413: card names the current team can't apply right now (e.g. Legendaries
   *  already used this week). Excluded cards get weight 0 in the spin RNG so the
   *  wheel never lands on a result that would just bounce off the backend limit. */
  excludedSabotageNames?: string[];
  /** SAB-415: dynamic catalog override. When provided, the wheel uses these
   *  options instead of the static SABOTAGE_OPTIONS / MLB_SABOTAGE_OPTIONS
   *  constants. Used by NFL surfaces to source real cards (with proper rarity
   *  tiers) from `nfl_sabotage_cards` so what the wheel displays matches what
   *  the server will actually assign. Each option's `id` must round-trip to
   *  the backend so the server can validate and apply the same card the user
   *  spun. Existing callers that don't pass this keep the static-catalog
   *  behaviour. */
  optionsOverride?: SabotageOption[];
}

const SabotageWheel = ({ onSabotageSelected, onSpinResult, deferApply = false, cost = 10, disabled = false, disabledMessage, excludedSabotageNames, optionsOverride }: SabotageWheelProps) => {
  const { activeSport } = useSport();
  const isMLB = activeSport === 'mlb';
  const [isSpinning, setIsSpinning] = useState(false);
  const [selectedSabotage, setSelectedSabotage] = useState<SabotageOption | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [rotation, setRotation] = useState(0);
  const wheelRef = useRef<SVGSVGElement>(null);
  const spinAudioPhase = useRef<'idle' | 'spinning' | 'landing'>('idle');

  // SAB-415: prefer the dynamic catalog when supplied; otherwise fall back to
  // the static per-sport defaults. Wheel internals (segment geometry, color
  // palette, weighted RNG) are catalog-agnostic.
  const options = optionsOverride && optionsOverride.length > 0
    ? optionsOverride
    : (isMLB ? MLB_SABOTAGE_OPTIONS : SABOTAGE_OPTIONS);
  const numSegments = options.length;
  const segmentAngle = 360 / numSegments;
  const cardWeights = useMemo(
    () => buildCardWeights(options, excludedSabotageNames ? new Set(excludedSabotageNames) : undefined),
    [options, excludedSabotageNames],
  );

  // Wheel dimensions — viewBox is 400x400, center at 200,200
  const CX = 200;
  const CY = 200;
  const OUTER_R = 178;
  const INNER_R = 42;
  const RING_R = 192;

  const spinWheel = () => {
    if (isSpinning) return;

    setIsSpinning(true);
    setSelectedSabotage(null);
    setShowResult(false);
    spinAudioPhase.current = 'spinning';
    trackEvent('spin_initiated', { cost });

    // Weighted random selection — rarity determines odds, then animate to that segment.
    // The wheel rotates clockwise, so parking segment i under the top pointer needs a
    // final resting angle of 360 - (i + 0.5) * segmentAngle. Compute the delta from the
    // wheel's current resting angle so accumulated rotation from prior spins never
    // shifts the landing off the weighted pick (or onto a segment boundary).
    const fullRotations = 6 + Math.floor(Math.random() * 4);
    const randomSegment = weightedRandomIndex(cardWeights);
    const currentAngle = ((rotation % 360) + 360) % 360;
    const targetAngle = 360 - (randomSegment + 0.5) * segmentAngle;
    const extraDegrees = ((targetAngle - currentAngle) % 360 + 360) % 360;
    const totalRotation = fullRotations * 360 + extraDegrees;

    const newRotation = rotation + totalRotation;
    setRotation(newRotation);

    // Wait for the CSS spin animation to finish
    setTimeout(() => {
      const normalizedRotation = ((newRotation % 360) + 360) % 360;
      const pointerAngle = (360 - normalizedRotation + 360) % 360;
      const selectedIndex = Math.floor(pointerAngle / segmentAngle) % numSegments;
      const selected = options[selectedIndex];

      spinAudioPhase.current = 'landing';
      setSelectedSabotage(selected);
      setIsSpinning(false);

      // Delay the result card pop for dramatic effect
      setTimeout(() => {
        setShowResult(true);
        spinAudioPhase.current = 'idle';
        trackEvent('spin_completed', { card_name: selected.name });
        if (deferApply) {
          onSpinResult?.(selected);
        } else {
          onSabotageSelected(selected);
        }
      }, 400);
    }, 5000);
  };

  // Create SVG path for a pie segment
  const createSegmentPath = (index: number, innerR: number, outerR: number) => {
    const startAngle = (index * segmentAngle - 90) * (Math.PI / 180);
    const endAngle = ((index + 1) * segmentAngle - 90) * (Math.PI / 180);

    const x1 = CX + outerR * Math.cos(startAngle);
    const y1 = CY + outerR * Math.sin(startAngle);
    const x2 = CX + outerR * Math.cos(endAngle);
    const y2 = CY + outerR * Math.sin(endAngle);
    const x3 = CX + innerR * Math.cos(endAngle);
    const y3 = CY + innerR * Math.sin(endAngle);
    const x4 = CX + innerR * Math.cos(startAngle);
    const y4 = CY + innerR * Math.sin(startAngle);

    return `M ${x1} ${y1} A ${outerR} ${outerR} 0 0 1 ${x2} ${y2} L ${x3} ${y3} A ${innerR} ${innerR} 0 0 0 ${x4} ${y4} Z`;
  };

  const getSegmentCenter = (index: number, radius: number) => {
    const angle = ((index + 0.5) * segmentAngle - 90) * (Math.PI / 180);
    return {
      x: CX + radius * Math.cos(angle),
      y: CY + radius * Math.sin(angle),
    };
  };

  // Vivid segment colors — cycle through palette for any number of segments
  const colorPalette: [string, string][] = [
    ['hsl(0, 80%, 50%)', 'hsl(0, 80%, 38%)'],       // Red
    ['hsl(270, 75%, 55%)', 'hsl(270, 75%, 42%)'],    // Purple
    ['hsl(25, 95%, 52%)', 'hsl(25, 95%, 40%)'],      // Orange
    ['hsl(150, 70%, 40%)', 'hsl(150, 70%, 30%)'],    // Green
    ['hsl(45, 90%, 50%)', 'hsl(45, 90%, 38%)'],      // Gold
    ['hsl(200, 80%, 50%)', 'hsl(200, 80%, 38%)'],    // Blue
    ['hsl(340, 80%, 55%)', 'hsl(340, 80%, 42%)'],    // Pink
    ['hsl(175, 70%, 42%)', 'hsl(175, 70%, 32%)'],    // Teal
    ['hsl(120, 65%, 42%)', 'hsl(120, 65%, 32%)'],    // Lime
    ['hsl(290, 65%, 50%)', 'hsl(290, 65%, 38%)'],    // Violet
  ];
  const segmentColors = options.map((_, i) => colorPalette[i % colorPalette.length]);

  // Short display names for the wheel — long names get clipped by segment boundaries
  const wheelLabel: Record<string, string> = {
    'Pitching Change': 'Pitch Change',
    'Seventh Inning Curse': '7th Inning',
    'Closer Curse': 'Closer Crse',
    'Error Jackpot': 'Err Jackpot',
    'Century Curse': 'Century',
    'Arm Breaker': 'Arm Break',
    'Clean Sweep': 'Clean Swp',
    'Point Freeze': 'Pt Freeze',
    'Butter Fingers': 'Butter Fgr',
    'INT Jackpot': 'INT Jkpot',
    "Fielder's Choice": "Flder's Ch",
    'Juiced Balls': 'Juiced',
  };

  // Text rotation to keep labels readable
  const getTextRotation = (index: number) => {
    const midAngleSvg = (index + 0.5) * segmentAngle - 90;
    const midAngleNormalized = ((midAngleSvg % 360) + 360) % 360;
    if (midAngleNormalized > 90 && midAngleNormalized <= 270) {
      return midAngleSvg + 180;
    }
    return midAngleSvg;
  };

  // The spin easing: slow start → fast middle → dramatic slow finish with overshoot
  const spinTransition = isSpinning
    ? 'transform 5s cubic-bezier(0.12, 0.75, 0.08, 1.01)'
    : 'none';

  return (
    <div className="flex flex-col items-center w-full max-w-[520px] mx-auto">
      {/* Wheel Container — generous vertical spacing */}
      <div className="relative w-full px-4 pt-8 pb-6">
        {/* Outer glow ring */}
        <div
          className={cn(
            "absolute rounded-full transition-all duration-700 pointer-events-none",
            isSpinning ? "animate-glow-ring" : "animate-glow-ring"
          )}
          style={{
            top: '50%',
            left: '50%',
            width: 'min(95%, 480px)',
            height: 'min(95%, 480px)',
            transform: 'translate(-50%, -50%)',
            borderRadius: '50%',
          }}
        />

        {/* Background ambient glow */}
        <div
          className={cn(
            "absolute rounded-full blur-3xl transition-all duration-1000 pointer-events-none",
            isSpinning ? "opacity-80 scale-110" : "opacity-40"
          )}
          style={{
            top: '50%',
            left: '50%',
            width: '80%',
            height: '80%',
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, hsl(25 95% 53% / 0.35) 0%, hsl(25 95% 53% / 0.08) 60%, transparent 80%)',
          }}
        />

        {/* Pointer — large dramatic triangle at top */}
        <div
          className={cn(
            "absolute z-30 left-1/2",
            isSpinning ? "" : "animate-pointer-bounce"
          )}
          style={{ top: 'calc(50% - min(47.5%, 240px) - 6px)', transform: 'translateX(-50%)' }}
        >
          <svg width="44" height="48" viewBox="0 0 44 48" className="drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)]">
            <defs>
              <linearGradient id="pointerGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(25, 95%, 60%)" />
                <stop offset="100%" stopColor="hsl(25, 95%, 42%)" />
              </linearGradient>
            </defs>
            <polygon
              points="22,48 0,0 44,0"
              fill="url(#pointerGrad)"
              stroke="hsl(25, 95%, 70%)"
              strokeWidth="1.5"
            />
            <polygon
              points="22,38 8,4 36,4"
              fill="hsl(25, 95%, 65%)"
              opacity="0.3"
            />
          </svg>
        </div>

        {/* Wheel SVG — fills available space */}
        <div
          className="relative mx-auto"
          style={{
            width: 'min(95%, 480px)',
            height: 'min(95%, 480px)',
          }}
        >
          <svg
            ref={wheelRef}
            width="100%"
            height="100%"
            viewBox="0 0 400 400"
            className="relative overflow-visible"
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: spinTransition,
              filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.5))',
            }}
          >
            <defs>
              <radialGradient id="centerGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="hsl(25, 95%, 55%)" />
                <stop offset="100%" stopColor="hsl(25, 95%, 35%)" />
              </radialGradient>
              <radialGradient id="centerShine" cx="40%" cy="35%" r="50%">
                <stop offset="0%" stopColor="rgba(255,255,255,0.25)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0)" />
              </radialGradient>
              {/* Segment gradients for depth */}
              {segmentColors.map(([light, dark], i) => (
                <radialGradient key={`sg-${i}`} id={`segGrad-${i}`} cx="50%" cy="50%" r="70%">
                  <stop offset="0%" stopColor={light} />
                  <stop offset="100%" stopColor={dark} />
                </radialGradient>
              ))}
              {/* Clip paths for text */}
              {options.map((_, index) => (
                <clipPath key={`clip-${index}`} id={`seg-clip-${index}`}>
                  <path d={createSegmentPath(index, INNER_R, OUTER_R)} />
                </clipPath>
              ))}
            </defs>

            {/* Outer decorative ring */}
            <circle cx={CX} cy={CY} r={RING_R} fill="none" stroke="hsl(25, 95%, 50%)" strokeWidth="3" opacity="0.6" />
            <circle cx={CX} cy={CY} r={RING_R + 4} fill="none" stroke="hsl(25, 95%, 50%)" strokeWidth="1" opacity="0.25" />

            {/* Tick marks on outer ring */}
            {Array.from({ length: numSegments * 4 }).map((_, i) => {
              const isMajor = i % 4 === 0;
              const a = (i * (360 / (numSegments * 4)) - 90) * (Math.PI / 180);
              const r1 = RING_R - (isMajor ? 8 : 4);
              const r2 = RING_R + 2;
              return (
                <line
                  key={`tick-${i}`}
                  x1={CX + r1 * Math.cos(a)}
                  y1={CY + r1 * Math.sin(a)}
                  x2={CX + r2 * Math.cos(a)}
                  y2={CY + r2 * Math.sin(a)}
                  stroke={isMajor ? "hsl(25, 95%, 60%)" : "hsl(25, 95%, 45%)"}
                  strokeWidth={isMajor ? 2 : 1}
                  opacity={isMajor ? 0.8 : 0.4}
                />
              );
            })}

            {/* Segments */}
            {options.map((option, index) => {
              const iconPos = getSegmentCenter(index, 95);
              const textPos = getSegmentCenter(index, 145);
              const textRot = getTextRotation(index);

              return (
                <g key={option.id}>
                  {/* Segment fill */}
                  <path
                    d={createSegmentPath(index, INNER_R, OUTER_R)}
                    fill={`url(#segGrad-${index})`}
                    stroke="rgba(0,0,0,0.4)"
                    strokeWidth="1.5"
                  />

                  {/* Highlight edge on each segment (inner bevel) */}
                  <path
                    d={createSegmentPath(index, INNER_R, INNER_R + 12)}
                    fill="rgba(255,255,255,0.06)"
                  />

                  {/* Icon — counter-rotates to stay upright */}
                  <text
                    x={iconPos.x}
                    y={iconPos.y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="28"
                    style={{
                      transform: `rotate(${-rotation}deg)`,
                      transformOrigin: `${iconPos.x}px ${iconPos.y}px`,
                    }}
                  >
                    {option.icon}
                  </text>

                  {/* Name label — clipped to segment, follows segment angle */}
                  <g clipPath={`url(#seg-clip-${index})`}>
                    <text
                      x={textPos.x}
                      y={textPos.y}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize="8"
                      fontWeight="800"
                      fill="white"
                      stroke="rgba(0,0,0,0.5)"
                      strokeWidth="0.4"
                      paintOrder="stroke"
                      letterSpacing="0.2"
                      style={{
                        transform: `rotate(${textRot}deg)`,
                        transformOrigin: `${textPos.x}px ${textPos.y}px`,
                        textTransform: 'uppercase',
                      }}
                    >
                      {wheelLabel[option.name] || option.name}
                    </text>
                  </g>
                </g>
              );
            })}

            {/* Center hub */}
            <circle cx={CX} cy={CY} r={INNER_R} fill="url(#centerGrad)" />
            <circle cx={CX} cy={CY} r={INNER_R} fill="url(#centerShine)" />
            <circle cx={CX} cy={CY} r={INNER_R - 3} fill="none" stroke="hsl(25, 95%, 70%)" strokeWidth="1.5" opacity="0.4" />
            {/* Skull emoji in center */}
            <text
              x={CX}
              y={CY}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="32"
              style={{
                transform: `rotate(${-rotation}deg)`,
                transformOrigin: `${CX}px ${CY}px`,
              }}
            >
              💀
            </text>
          </svg>
        </div>
      </div>

      {/* Card legend — pill-shaped chips */}
      <div className="flex flex-wrap justify-center gap-1.5 px-3 pb-4 w-full max-w-[500px]">
        {options.map((option, index) => (
          <div
            key={option.id}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-border/50 bg-muted/30 hover:bg-muted/50 transition-colors"
            style={{
              borderColor: segmentColors[index][0] + '60',
              background: `linear-gradient(135deg, ${segmentColors[index][1]}15, transparent)`,
            }}
          >
            <span className="text-sm leading-none">{option.icon}</span>
            <span className="text-[11px] font-semibold text-foreground/80 whitespace-nowrap">{option.name}</span>
          </div>
        ))}
      </div>

      {/* Spin Button */}
      <div className="flex flex-col items-center gap-1">
        <Button
          onClick={spinWheel}
          disabled={isSpinning || disabled}
          size="lg"
          className={cn(
            "px-10 py-6 text-lg font-bold rounded-xl transition-all duration-300",
            "bg-gradient-to-b from-orange-500 to-orange-700 hover:from-orange-400 hover:to-orange-600",
            "shadow-[0_4px_20px_rgba(234,88,12,0.4)] hover:shadow-[0_6px_28px_rgba(234,88,12,0.5)]",
            "border border-orange-400/30",
            (isSpinning || disabled) && "opacity-60 cursor-not-allowed"
          )}
        >
          {isSpinning ? (
            <span className="flex items-center gap-2">
              <span className="inline-block animate-spin text-xl">🎰</span>
              Spinning...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <span className="text-xl">🎯</span>
              Spin for ${cost}
            </span>
          )}
        </Button>
        {disabled && disabledMessage && (
          <p className="text-xs text-muted-foreground text-center">{disabledMessage}</p>
        )}
      </div>

      {/* Result Modal — overlay with dismiss (hidden in deferred mode — parent handles) */}
      {selectedSabotage && showResult && !deferApply && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          onClick={() => { setShowResult(false); setSelectedSabotage(null); }}
        >
          {/* Dark backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

          {/* Card */}
          <div
            className="animate-result-pop relative w-[90%] max-w-sm mx-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={cn(
                "relative overflow-hidden rounded-2xl p-6 text-center",
                "bg-gradient-to-b from-card to-card/80",
                "border-2 border-orange-500/50",
                "shadow-[0_0_40px_rgba(234,88,12,0.3),0_0_80px_rgba(234,88,12,0.1)]"
              )}
            >
              {/* Close button */}
              <button
                onClick={() => { setShowResult(false); setSelectedSabotage(null); }}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-muted/60 hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors z-10"
              >
                ✕
              </button>

              {/* Sparkle background effect */}
              <div className="absolute inset-0 opacity-10 pointer-events-none"
                style={{
                  background: 'radial-gradient(circle at 30% 20%, hsl(25 95% 60%) 0%, transparent 50%), radial-gradient(circle at 70% 80%, hsl(45 90% 55%) 0%, transparent 50%)',
                }}
              />

              <div className="relative">
                <span className="text-6xl block mb-3">{selectedSabotage.icon}</span>
                <h3 className="text-2xl font-bold text-gradient-sabotage mb-2">
                  {selectedSabotage.name}
                </h3>
                <p className="text-sm text-muted-foreground mb-3">{selectedSabotage.description}</p>
                <div className="inline-block px-4 py-1.5 rounded-full bg-damage/15 border border-damage/30">
                  <span className="text-base font-bold text-damage">{selectedSabotage.effect}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SabotageWheel;
